import { IEvaServiceDefinition } from '@springtree/eva-services-core';
import { AnyAction } from 'redux';
import { IEvaServiceState } from '../state';

/**
 * The data payload for an EVA Reset Redux action
 *
 * @export
 * @interface IEvaServiceResetActionPayload
 * @extends {AnyAction}
 * @template SVC
 */
export interface IEvaServiceResetActionPayload<SVC extends IEvaServiceDefinition> {
  state?: IEvaServiceState<SVC>;
}

export interface IEvaServiceResetAction<SVC extends IEvaServiceDefinition, TYPE = string> extends AnyAction {
  type: TYPE;
  payload: IEvaServiceResetActionPayload<SVC>;
}

/**
 * The required payload for making a response action
 *
 * @export
 * @interface ICreateServiceResetAction
 * @template SVC
 */
export interface ICreateServiceResetActionParams<SVC extends IEvaServiceDefinition> {
  /**
   * Reset actions are always for a specific state key
   *
   * @type {string}
   */
  stateKey: string;

  /**
   * The typed response payload
   *
   * @type {IEvaServiceState<SVC>}
   */
  state?: IEvaServiceState<SVC>;
}

/**
 * Helper method to create a typed EVA Service response action
 *
 * @export
 * @template SVC
 * @param {ICreateServiceResetAction<SVC>} params The response action parameters
 * @returns
 */
export function createServiceResetAction<SVC extends IEvaServiceDefinition>(
  params: ICreateServiceResetActionParams<SVC>,
) {

  // Combine all supplied parameters into the action payload
  //
  const payload: IEvaServiceResetActionPayload<SVC> = {
    state: params.state,
  };

  // Build the final action object
  //
  const action: IEvaServiceResetAction<SVC> = {
    payload,
    type: `${params.stateKey}:RESET`,
  };

  return action;
}
